// Validation functions for checkout forms

export const validateAddress = (shippingAddress, email, billingAddress, useSameAsShipping) => {
    const newErrors = {};

    if (!email.trim()) {
        newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        newErrors.email = 'Invalid email format';
    }

    if (!shippingAddress.firstname.trim()) {
        newErrors.firstname = 'First name is required';
    }

    if (!shippingAddress.lastname.trim()) {
        newErrors.lastname = 'Last name is required';
    }

    if (!shippingAddress.street[0]?.trim()) {
        newErrors.street = 'Street address is required';
    }

    if (!shippingAddress.city.trim()) {
        newErrors.city = 'City is required';
    }

    if (!shippingAddress.region_id) {
        newErrors.region = 'State selection is required';
    }

    if (!shippingAddress.postcode.trim()) {
        newErrors.postcode = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(shippingAddress.postcode.trim())) {
        newErrors.postcode = 'Invalid ZIP code format';
    }

    if (!shippingAddress.telephone.trim()) {
        newErrors.telephone = 'Phone number is required';
    } else if (!/^\d{10,}$/.test(shippingAddress.telephone.trim())) {
        newErrors.telephone = 'Phone number must be at least 10 digits';
    }

    // Validate billing address if not using same as shipping
    if (!useSameAsShipping && billingAddress) {
        if (!billingAddress.firstname.trim()) {
            newErrors.billing_firstname = 'Billing first name is required';
        }

        if (!billingAddress.lastname.trim()) {
            newErrors.billing_lastname = 'Billing last name is required';
        }

        if (!billingAddress.street[0]?.trim()) {
            newErrors.billing_street = 'Billing street address is required';
        }

        if (!billingAddress.city.trim()) {
            newErrors.billing_city = 'Billing city is required';
        }

        if (!billingAddress.region_id) {
            newErrors.billing_region = 'Billing state selection is required';
        }

        if (!billingAddress.postcode.trim()) {
            newErrors.billing_postcode = 'Billing ZIP code is required';
        } else if (!/^\d{5}(-\d{4})?$/.test(billingAddress.postcode.trim())) {
            newErrors.billing_postcode = 'Invalid billing ZIP code format';
        }

        if (!billingAddress.telephone.trim()) {
            newErrors.billing_telephone = 'Billing phone number is required';
        } else if (!/^\d{10,}$/.test(billingAddress.telephone.trim())) {
            newErrors.billing_telephone = 'Billing phone number must be at least 10 digits';
        }
    }

    return newErrors;
};

export const validateAuthorizeNetCardData = (cardData) => {
    const newErrors = {};

    // Card number validation with Luhn algorithm
    if (!cardData.number || !cardData.number.trim()) {
        newErrors.cardNumber = 'Card number is required';
    } else {
        const cardNumber = cardData.number.replace(/\s/g, '');
        if (cardNumber.length < 13 || cardNumber.length > 19) {
            newErrors.cardNumber = 'Card number must be 13-19 digits';
        } else if (!luhnCheck(cardNumber)) {
            newErrors.cardNumber = 'Invalid card number';
        }
    }

    // Cardholder name validation
    if (!cardData.holderName || !cardData.holderName.trim()) {
        newErrors.cardHolderName = 'Cardholder name is required';
    } else if (cardData.holderName.trim().length < 2) {
        newErrors.cardHolderName = 'Please enter a valid name';
    }

    // Expiry date validation (MM/YY format)
    if (!cardData.expiry || !cardData.expiry.trim()) {
        newErrors.cardExpiry = 'Expiry date is required';
    } else {
        const [month, year] = cardData.expiry.split('/');
        if (!month || !year || month.length !== 2 || year.length !== 2) {
            newErrors.cardExpiry = 'Invalid expiry format (MM/YY)';
        } else {
            const currentDate = new Date();
            const currentMonth = currentDate.getMonth() + 1;
            const currentYear = currentDate.getFullYear() % 100;
            const expMonth = parseInt(month, 10);
            const expYear = parseInt(year, 10);

            if (expMonth < 1 || expMonth > 12) {
                newErrors.cardExpiry = 'Invalid month';
            } else if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
                newErrors.cardExpiry = 'Card has expired';
            }
        }
    }

    // CVV validation
    if (!cardData.cvv || !cardData.cvv.trim()) {
        newErrors.cardCvv = 'CVV is required';
    } else {
        const cardNumber = (cardData.number || '').replace(/\s/g, '');
        // American Express cards have 4-digit CVV
        const isAmex = cardNumber.startsWith('34') || cardNumber.startsWith('37');
        const requiredLength = isAmex ? 4 : 3;

        if (cardData.cvv.length !== requiredLength) {
            newErrors.cardCvv = `CVV must be ${requiredLength} digits${isAmex ? ' for American Express' : ''}`;
        }
    }

    return newErrors;
};

export const validateCardData = (cardData) => {
    const newErrors = {};

    // Card number validation with Luhn algorithm
    if (!cardData.number.trim()) {
        newErrors.cardNumber = 'Card number is required';
    } else {
        const cardNumber = cardData.number.replace(/\s/g, '');
        if (cardNumber.length < 13 || cardNumber.length > 19) {
            newErrors.cardNumber = 'Card number must be 13-19 digits';
        } else if (!luhnCheck(cardNumber)) {
            newErrors.cardNumber = 'Invalid card number';
        }
    }

    // Cardholder name validation
    if (!cardData.holderName.trim()) {
        newErrors.cardHolderName = 'Cardholder name is required';
    } else if (cardData.holderName.trim().length < 2) {
        newErrors.cardHolderName = 'Please enter a valid name';
    }

    // Expiry month and year validation
    if (!cardData.expiryMonth || !cardData.expiryYear) {
        newErrors.cardExpiry = 'Expiry date is required';
    } else {
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = currentDate.getFullYear();
        const expMonth = parseInt(cardData.expiryMonth, 10);
        const expYear = parseInt(cardData.expiryYear, 10);

        if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
            newErrors.cardExpiry = 'Card has expired';
        }
    }

    // CVV validation
    if (!cardData.cvv.trim()) {
        newErrors.cardCvv = 'CVV is required';
    } else {
        const cardNumber = cardData.number.replace(/\s/g, '');
        // American Express cards have 4-digit CVV
        const isAmex = cardNumber.startsWith('34') || cardNumber.startsWith('37');
        const requiredLength = isAmex ? 4 : 3;

        if (cardData.cvv.length !== requiredLength) {
            newErrors.cardCvv = `CVV must be ${requiredLength} digits${isAmex ? ' for American Express' : ''}`;
        }
    }

    return newErrors;
};

// Luhn algorithm for card number validation
export const luhnCheck = (cardNumber) => {
    let sum = 0;
    let alternate = false;

    for (let i = cardNumber.length - 1; i >= 0; i--) {
        let digit = parseInt(cardNumber.charAt(i), 10);

        if (alternate) {
            digit *= 2;
            if (digit > 9) {
                digit = (digit % 10) + 1;
            }
        }

        sum += digit;
        alternate = !alternate;
    }

    return (sum % 10) === 0;
};

// Generate Braintree payment method nonce
export const generateBraintreeNonce = async () => {
    try {
        // This is a placeholder implementation
        // In a real application, you would:
        // 1. Load Braintree client SDK
        // 2. Initialize the client with merchant configuration
        // 3. Use the client to tokenize the card data
        // 4. Return the payment method nonce

        // For testing purposes only - this will still fail in production
        // You need to implement proper Braintree integration

        // Temporary workaround: Use a test nonce for development
        // This should be replaced with real Braintree integration
        console.warn('Using temporary Braintree nonce - implement proper integration');

        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 500));

        // Return null to trigger fallback to check/money order
        return null;
    } catch (error) {
        console.error('Error generating Braintree nonce:', error);
        return null;
    }
};